import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { UserRound } from 'lucide-react'
import { useSession } from '../lib/session'
import { useDismiss } from '../lib/hooks'
import { backendOverrideActive, burrowAppUrl, setBackendOverride } from '../lib/env'
import { displayName } from '../lib/text'
import { Avatar } from './ui'

/**
 * The nav's person button (the app's profile button) and what Writings needs
 * behind it: who you are, your profile in Burrow, which database this is (the
 * old "Demo" badge's job), and signing out.
 */
export function AccountMenu() {
  const { backend, profile, viewer, config } = useSession()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)
  if (!viewer) return null

  const item = 'rounded-lg px-3 py-2 text-left text-foreground no-underline hover:bg-muted/40'
  const switchBackend = (value: 'local' | null) => {
    setBackendOverride(value)
    window.location.assign('/')
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        title="Profile"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-center rounded-xl p-2.5 text-muted-foreground transition-all hover:bg-muted/30 hover:text-foreground ${open ? 'bg-muted/30 text-foreground' : ''}`}
      >
        <UserRound size={17} aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-12 z-50 flex w-64 flex-col gap-0.5 rounded-2xl border border-border/50 bg-card p-1.5 text-[13px] font-medium shadow-[var(--shadow-pop)]">
          <div className="flex items-center gap-2.5 px-3 py-2">
            <Avatar src={profile?.avatar_url} name={profile?.display_name} size={30} />
            <div className="min-w-0">
              <div className="truncate font-display text-[14px] text-foreground">{displayName(profile)}</div>
              <div className="truncate text-[11.5px] font-normal text-muted-foreground">{profile?.handle ? `@${profile.handle}` : viewer.email}</div>
            </div>
          </div>
          <a role="menuitem" href={`${burrowAppUrl}/u/me`} className={item}>
            Your profile
          </a>
          <div className="mx-2 my-1 h-px bg-border/60" />
          <p className="m-0 px-3 py-1.5 text-[11.5px] font-normal text-muted-foreground">
            {config.mode === 'supabase' ? 'Connected to the dev database.' : `Local demo data. ${config.reason}`}
          </p>
          {config.mode === 'supabase' && (
            <button type="button" role="menuitem" className={item} onClick={() => switchBackend('local')}>
              Use local demo data
            </button>
          )}
          {config.mode === 'local' && backendOverrideActive && (
            <button type="button" role="menuitem" className={item} onClick={() => switchBackend(null)}>
              Use the dev database
            </button>
          )}
          {config.mode === 'supabase' && (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={async () => {
                await backend.signOut()
                navigate('/signin')
              }}
            >
              Sign out
            </button>
          )}
        </div>
      )}
    </div>
  )
}
